/**
 * Utility functions for handling clipboard operations (copy/cut) for badges
 */

/**
 * Helper function to check if a node is inside a badge element
 */
const isInsideBadge = (node: Node, root: DocumentFragment): boolean => {
  let current: Node | null = node
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as HTMLElement
      if (
        element.classList.contains('linkedText') ||
        element.classList.contains('embeddedImage') ||
        element.classList.contains('embeddedVideo')
      ) {
        return true
      }
    }
    current = current.parentNode
  }
  return false
}

/**
 * Extract markdown text from cloned contents (for copy/cut operations)
 * Returns the markdown string representation of badges and text
 */
export const extractMarkdownFromSelection = (
  clonedContents: DocumentFragment,
): string => {
  const allParts: string[] = []

  const walker = document.createTreeWalker(
    clonedContents,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: node => {
        // Skip all nodes inside badge elements (we only want the markdown, not the text content)
        if (isInsideBadge(node, clonedContents)) {
          // But accept the badge element itself
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            ((node as HTMLElement).classList.contains('linkedText') ||
              (node as HTMLElement).classList.contains('embeddedImage') ||
              (node as HTMLElement).classList.contains('embeddedVideo'))
          ) {
            return NodeFilter.FILTER_ACCEPT
          }
          return NodeFilter.FILTER_REJECT
        }
        return NodeFilter.FILTER_ACCEPT
      },
    },
  )

  let node: Node | null = walker.nextNode()
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text) {
        allParts.push(text)
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement
      if (
        element.classList.contains('linkedText') ||
        element.classList.contains('embeddedImage') ||
        element.classList.contains('embeddedVideo')
      ) {
        const markdown = element.getAttribute('data-markdown')
        if (markdown) {
          allParts.push(markdown)
        }
      }
    }
    node = walker.nextNode()
  }

  return allParts.join(' ')
}

/**
 * Check if selection contains any badges
 */
export const selectionContainsBadges = (
  clonedContents: DocumentFragment,
): boolean => {
  const badgeElements = clonedContents.querySelectorAll(
    '.linkedText, .embeddedImage, .embeddedVideo',
  )
  return badgeElements.length > 0
}

/**
 * Get HTML string from cloned contents for HTML clipboard format
 */
export const getHtmlFromClonedContents = (
  clonedContents: DocumentFragment,
): string => {
  const tempContainer = document.createElement('div')
  tempContainer.appendChild(clonedContents.cloneNode(true))
  return tempContainer.innerHTML
}
